const CookieBanner = require("./CookieBanner");
const CookieModal = require("./CookieModal");
const CookieConsentAjax = require("./CookieConsentAjax");

class CookieConsent {
  constructor(settings) {
    this.settings = settings;
    this.consent = {}; // Initialize consent state
    this.cookiePrefix = "mad_consent_"; // Define a consistent prefix
    this.ajax = new CookieConsentAjax(settings.nonce, settings.ajaxurl);

    // Pass this instance to subcomponents
    this.banner = new CookieBanner(this);
    this.modal = new CookieModal(this);

    this.init();
  }

  init() {
    this.readConsentFromCookies();
    this.checkAllVideoConsents();
    this.listenForVideoConsent();
    this.applyConsent();

    if (this.getCookie("hide_cookie_banner") === "true") {
      this.banner.hide();
    } else {
      this.banner.show();
    }
  }

  getConsent() {
    return this.consent || {};
  }

  checkAllVideoConsents() {
    const consent = this.getConsent();
    const triggeredTypes = this._triggeredVideoTypes || {};
    const videoConsentMap = this.settings?.videoRequiredCookies || {};

    // Ensure the global object exists
    if (!window.madVideoConsentGiven) {
      window.madVideoConsentGiven = {};
    }

    Object.entries(videoConsentMap).forEach(([type, cookies]) => {
      const allAccepted = cookies.every((cookieName) => {
        const category = this.getCategoryByCookie(cookieName);
        return consent[category]?.[cookieName] === true;
      });

      if (allAccepted && !triggeredTypes[type]) {
        triggeredTypes[type] = true;
        window.madVideoConsentGiven[type] = true;
        jQuery(document).trigger(`madVideoConsentGiven:${type}`);
      }
    });

    this._triggeredVideoTypes = triggeredTypes;
  }

  listenForVideoConsent() {
    jQuery(document).on("madRequestVideoConsent", (event, data) => {
      const type = data?.type;
      if (!type) return;

      const cookies = this.settings?.videoRequiredCookies?.[type] || [];

      cookies.forEach((cookieName) => {
        this.giveCookieConsent(cookieName);
      });
    });
  }

  readConsentFromCookies() {
    const consent = {};
    const categoryCookieMap = this.settings.categoryCookieMap;

    Object.keys(categoryCookieMap).forEach((categorySlug) => {
      const categoryConsent = {};

      this.getCookiesByCategory(categorySlug).forEach((cookieSlug) => {
        if (this.checkCookie("cookie_" + cookieSlug)) {
          categoryConsent[cookieSlug] = true;
        }
      });

      if (Object.keys(categoryConsent).length > 0) {
        consent[categorySlug] = categoryConsent;
      }
    });

    this.consent = consent;
  }

  acceptAll() {
    Object.keys(this.settings.categoryCookieMap).forEach((categorySlug) => {
      this.giveCookieCategoryConsent(categorySlug);
    });

    this.setCookie("hide_cookie_banner", "true", 365);
    this.applyConsent();
    this.logConsent();
    this.banner.hide();
    this.modal.hide();
  }

  getVideoConsentStatus() {
    const videoRequiredCookies =
      window.$madSettings?.cookieConsent?.videoRequiredCookies || {};
    const consent = this.consent || {};

    return Object.entries(videoRequiredCookies).every(([type, cookies]) =>
      cookies.every((cookieName) => {
        const categorySlug = this.getCategoryByCookie(cookieName);
        return consent[categorySlug]?.[cookieName] === true;
      })
    );
  }

  giveCookieConsent(cookieName) {
    const categorySlug = this.getCategoryByCookie(cookieName);
    if (!this.consent[categorySlug]) this.consent[categorySlug] = {};
    this.consent[categorySlug][cookieName] = true;
    this.setCookie("cookie_" + cookieName, "true", 365);
    this.onCookieConsentGiven(cookieName);
  }

  giveCookieCategoryConsent(categorySlug) {
    const cookies = this.getCookiesByCategory(categorySlug);
    if (!this.consent[categorySlug]) this.consent[categorySlug] = {};

    if (cookies && cookies.length > 0) {
      cookies.forEach((cookieName) => {
        this.consent[categorySlug][cookieName] = true;
        this.setCookie("cookie_" + cookieName, "true", 365);
        this.onCookieConsentGiven(cookieName);
      });
    }

    this.setCookie("category_" + categorySlug, "true", 365);
    this.onCookieCategoryConsentGiven(categorySlug);
  }

  revokeCookieConsent(cookieName) {
    const categorySlug = this.getCategoryByCookie(cookieName);
    if (!this.consent[categorySlug]) {
      this.consent[categorySlug] = {};
    }
    this.consent[categorySlug][cookieName] = false;
    this.setCookie("cookie_" + cookieName, "false", 365);
    this.onCookieConsentRevoked(cookieName);
  }

  revokeCookieCategoryConsent(categorySlug) {
    const cookies = this.getCookiesByCategory(categorySlug);
    if (!this.consent[categorySlug]) {
      this.consent[categorySlug] = {};
    }
    if (cookies && cookies.length > 0) {
      cookies.forEach((cookieName) => {
        this.consent[categorySlug][cookieName] = false;
        this.setCookie("cookie_" + cookieName, "false", 365);
        this.onCookieConsentRevoked(cookieName);
      });
    }
    this.setCookie("category_" + categorySlug, "false", 365);
    this.onCookieCategoryConsentRevoked(categorySlug);
  }

  getCookieConsent(cookieName) {
    return this.checkCookie("cookie_" + cookieName);
  }

  getCookieCategoryConsent(categorySlug) {
    return this.checkCookie("category_" + categorySlug);
  }

  setCookie(name, value, days) {
    name = this.cookiePrefix + name;
    let expires = "";
    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 86400000); // 24*60*60*1000
      expires = "; expires=" + date.toUTCString();
    }
    document.cookie = `${name}=${value || ""}${expires}; path=/`;
  }

  getCookie(name) {
    name = this.cookiePrefix + name;
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let c of ca) {
      while (c.charAt(0) === " ") c = c.substring(1);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }

  checkCookie(name) {
    const cookieValue = this.getCookie(name);
    return cookieValue === "true";
  }

  deleteCookie(name) {
    name = this.cookiePrefix + name;
    document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Path=/;`;
  }

  applyConsent() {
    Object.keys(this.consent).forEach((categorySlug) => {
      Object.keys(this.consent[categorySlug]).forEach((cookieSlug) => {
        if (this.consent[categorySlug][cookieSlug]) {
          this.activateScriptsByCookieName(cookieSlug);
        } else {
          this.deactivateScriptsByCookieName(cookieSlug);
        }
      });
    });
  }

  activateScriptsByCookieName(cookieName) {
    jQuery(
      `script[data-cookie="${cookieName}"][type="application/script-template"]`
    ).each(function () {
      const script = jQuery(this);
      const newScript = jQuery("<script>", {
        type: "application/javascript",
        html: script.html(),
      });

      jQuery.each(this.attributes, function () {
        if (this.name !== "type") {
          newScript.attr(this.name, this.value);
        }
      });

      script.replaceWith(newScript);
    });
  }

  deactivateScriptsByCookieName(cookieName) {
    jQuery(
      `script[data-cookie="${cookieName}"][type="application/javascript"]`
    ).remove();
  }

  isVideoConsentCookie(cookieSlug) {
    const videoRequiredCookies =
      window.$madSettings?.cookieConsent?.videoRequiredCookies || {};
    return Object.values(videoRequiredCookies).flat().includes(cookieSlug);
  }

  onCookieConsentGiven(cookieSlug) {
    this.activateScriptsByCookieName(cookieSlug);
    this.setCookie("hide_cookie_banner", "true", 365);

    this.checkAllVideoConsents();
  }

  onCookieConsentRevoked(cookieSlug) {
    this.deactivateScriptsByCookieName(cookieSlug);
  }

  onCookieCategoryConsentGiven(categorySlug) {
    this.setCookie("hide_cookie_banner", "true", 365);
    this.activateScriptsByCategoryName(categorySlug);
  }

  onCookieCategoryConsentRevoked(categorySlug) {
    this.deactivateScriptsByCategoryName(categorySlug);
  }

  activateScriptsByCategoryName(categoryName) {
    jQuery(
      `script[data-cookie-category="${categoryName}"][type="application/script-template"]`
    ).each(function () {
      const script = jQuery(this);
      const newScript = jQuery("<script>", {
        type: "application/javascript",
        html: script.html(),
      });

      jQuery.each(this.attributes, function () {
        if (this.name !== "type") {
          newScript.attr(this.name, this.value);
        }
      });

      script.replaceWith(newScript);
    });
  }

  deactivateScriptsByCategoryName(categoryName) {
    jQuery(
      `script[data-cookie-category="${categoryName}"][type="application/javascript"]`
    ).remove();
  }

  getCategoryByCookie(cookieSlug) {
    return this.settings.cookieCategoryMap[cookieSlug];
  }

  getCookiesByCategory(categorySlug) {
    return this.settings.categoryCookieMap[categorySlug];
  }

  logConsent() {
    const allCategories = Object.keys(this.settings.categoryCookieMap);
    const consentData = { consent: {} };

    allCategories.forEach((categorySlug) => {
      const cookies = this.getCookiesByCategory(categorySlug);
      if (cookies && cookies.length > 0) {
        consentData.consent[categorySlug] = {};
        cookies.forEach((cookieName) => {
          const consentGiven = !!(
            this.consent[categorySlug] && this.consent[categorySlug][cookieName]
          );
          consentData.consent[categorySlug][cookieName] = consentGiven;
        });
      }
    });

    this.ajax.logConsent("consent_given", consentData);
  }

  getCategoryByCookie(cookieSlug) {
    return this.settings.cookieCategoryMap[cookieSlug];
  }

  getCookiesByCategory(categorySlug) {
    return this.settings.categoryCookieMap[categorySlug];
  }
}

module.exports = CookieConsent;
